// Tytuł karty przeglądarki — JEDNO miejsce: trasa → nazwa strony, format
// „<strona> · Legia Hub". Strony szczegółów dopisują konkret, gdy go znają
// (`usePageTitle`: mecz, rywal, zawodnik). Trasy /print też tu są — Chromium bierze
// document.title jako tytuł PDF-u, więc wydruki dostają sensowny opis w metadanych.
// Nowa trasa w main.tsx = nowy wpis (pilnuje tego pageTitle.test.ts).

import { createContext, useContext, useEffect, type Dispatch, type SetStateAction } from 'react'
import { useLocation } from 'react-router-dom'

import { matchPattern } from './access'

export const APP_NAME = 'Legia Hub'

// Pierwszy pasujący wzorzec wygrywa — węższe przed szerszymi (jak w lib/access.ts).
const TITLES: { pattern: string; title: string }[] = [
  { pattern: '/home', title: 'Start' },
  // Raporty Meczowe
  { pattern: '/raporty/podsumowanie/*', title: 'Podsumowanie okresowe' },
  { pattern: '/raporty/ranking', title: 'Podsumowanie okresowe' },
  { pattern: '/raporty/live', title: 'LIVE' },
  { pattern: '/raporty/live/:id/print', title: 'Raport LIVE' },
  { pattern: '/raporty/live/:id', title: 'Sesja LIVE' },
  { pattern: '/raporty/sfg', title: 'SFG w sezonie' },
  { pattern: '/raporty/legia-ii', title: 'Raporty Meczowe · Legia II' },
  { pattern: '/raporty/legia-ii/*', title: 'Raport pomeczowy · Legia II' },
  { pattern: '/raporty', title: 'Raporty Meczowe' },
  { pattern: '/raporty/*', title: 'Raport pomeczowy' },
  // Analiza Rywala (Sztab) / Skauting (Scout, Dyrektor)
  { pattern: '/liga', title: 'Profil ligi' },
  { pattern: '/liga/ranking', title: 'Ranking zawodników ligi' },
  { pattern: '/liga/motoryka', title: 'Profil motoryczny ligi' },
  { pattern: '/dossier', title: 'Drużyny' },
  { pattern: '/dossier/:id/motoryka/print', title: 'Ocena motoryczna rywala' },
  { pattern: '/dossier/*', title: 'Analiza Rywala' },
  { pattern: '/gracze', title: 'Zawodnicy ligi' },
  { pattern: '/gracze/porownanie', title: 'Porównanie zawodników' },
  { pattern: '/gracze/*', title: 'Profil zawodnika' },
  { pattern: '/mapa/*', title: 'Mapa zawodników' },
  { pattern: '/sezon', title: 'Sezon' },
  { pattern: '/szanse', title: 'Szanse' },
  { pattern: '/szanse/*', title: 'Szanse meczu' },
  // Trening
  { pattern: '/raport-trening/*', title: 'Raport treningowy' },
  { pattern: '/raport-mikrocykl/print', title: 'Raport mikrocyklu' },
  { pattern: '/trening', title: 'Wbijanie metryk' },
  { pattern: '/bramkarze', title: 'Bramkarze' },
  // Motoryka
  { pattern: '/motoryka', title: 'Motoryka' },
  { pattern: '/raport-motoryka-dzienne/print', title: 'Motoryka — raport dzienny' },
  { pattern: '/raport-motoryka-zawodnicy/print', title: 'Motoryka — zawodnicy' },
  { pattern: '/raport-motoryka-porownanie/print', title: 'Motoryka — porównanie mikrocykli' },
  { pattern: '/raport-motoryka-mecz-porownanie/print', title: 'Motoryka — porównanie meczów' },
  { pattern: '/raport-motoryka-pomeczowy/print', title: 'Motoryka — podsumowanie meczowe' },
  { pattern: '/raport-motoryka-ocena/print', title: 'Motoryka — ocena motoryczna' },
  { pattern: '/raport-motoryka-liga/print', title: 'Motoryka — zespół na tle ligi' },
  // VALD (karta zawodnika doprecyzowuje tytuł nazwiskiem przez usePageTitle)
  { pattern: '/vald/zawodnicy/:id/raport/print', title: 'VALD — raport dla zawodnika' },
  { pattern: '/vald/zawodnicy/:id/raport', title: 'VALD — raport dla zawodnika' },
  { pattern: '/vald/zawodnicy/:id', title: 'VALD — karta zawodnika' },
  { pattern: '/vald/zawodnicy', title: 'VALD — zawodnicy' },
  { pattern: '/vald/dzien', title: 'VALD — dzień testowy' },
  { pattern: '/vald/skoki/*', title: 'VALD — skoki' },
  { pattern: '/vald/sila/*', title: 'VALD — siła' },
  { pattern: '/vald/interpretacja', title: 'VALD — interpretacja CMJ' },
  { pattern: '/vald/testy/*', title: 'VALD — testy' },
  { pattern: '/vald', title: 'VALD' },
  // Zawodnicy (Indywidualna Karta Zawodnika)
  { pattern: '/karta-zawodnika', title: 'Indywidualna Karta Zawodnika' },
  { pattern: '/akademia', title: 'Akademia' },
  { pattern: '/akademia/kadra', title: 'Akademia — kadra' },
  { pattern: '/akademia/:profileId', title: 'Karta zawodnika akademii' },
  { pattern: '/karta-zawodnika/*', title: 'Karta zawodnika' },
  { pattern: '/rankingi/*', title: 'Rankingi kadry' },
  { pattern: '/procedury', title: 'Procedury' },
  { pattern: '/kalendarz', title: 'Kalendarz' },
  // Konta i ukryte sekcje
  { pattern: '/konto/ustaw-haslo', title: 'Ustaw hasło' },
  { pattern: '/konto', title: 'Moje konto' },
  { pattern: '/admin', title: 'Panel admina' },
  { pattern: '/squad-planner', title: 'Squad Planner' },
]

/** Nazwa strony dla ścieżki; null = sama nazwa aplikacji. */
export function routeTitle(pathname: string): string | null {
  return TITLES.find((t) => matchPattern(t.pattern, pathname))?.title ?? null
}

/** „Legia Warszawa – Wisła Kraków 2:0" — gospodarz pierwszy, wynik w tej samej kolejności. */
export function matchTitle(m: { venue: 'home' | 'away'; team_name: string; opposition_name: string; goals_for: number; goals_against: number }): string {
  return m.venue === 'home'
    ? `${m.team_name} – ${m.opposition_name} ${m.goals_for}:${m.goals_against}`
    : `${m.opposition_name} – ${m.team_name} ${m.goals_against}:${m.goals_for}`
}

/** „Raport pomeczowy · Legia Hub"; bez nazwy strony — „Legia Hub". */
export function documentTitle(page: string | null): string {
  return page ? `${page} · ${APP_NAME}` : APP_NAME
}

/** Konkret strony szczegółów — przypięty do ścieżki, żeby nie przeciekł na następną. */
export interface PageTitleOverride { path: string; title: string }

export const PageTitleContext = createContext<Dispatch<SetStateAction<PageTitleOverride | null>>>(() => {})

/**
 * Doprecyzowanie tytułu karty, gdy strona zna już swój obiekt („Legia – Wisła
 * Kraków 2:0", nazwa rywala, zawodnika). null / pusty = tytuł z mapy tras.
 */
export function usePageTitle(title: string | null | undefined): void {
  const setOverride = useContext(PageTitleContext)
  const { pathname } = useLocation()
  useEffect(() => {
    if (!title) return
    const mine = { path: pathname, title }
    setOverride(mine)
    return () => setOverride((cur) => (cur === mine ? null : cur))
  }, [title, pathname, setOverride])
}
